import React from "react";
import { Link } from "react-router-dom";
import logo from "../assets/integrity-lifeline-logo.png";

export default function Home() {
    const features = [
      {
        title: "AI Credit Report Analysis",
        text: "Review uploaded credit reports, identify dispute candidates, surface risks, and organize recommended actions from one workspace.",
      },
      {
        title: "Client Management",
        text: "Manage client profiles, contact details, case activity, documents, disputes, notes, and workflow history.",
      },
      {
        title: "Dispute Letter Workflow",
        text: "Generate, review, track, and manage dispute letters through multiple rounds with status and follow-up dates.",
      },
      {
        title: "Account Inspector",
        text: "Inspect individual tradelines, review account issues, and route accounts into the appropriate dispute strategy.",
      },
      {
        title: "Potential Violation Tracking",
        text: "Document potential credit reporting compliance issues, preserve dispute history and supporting evidence, and track bureau or furnisher responses that may require further review.",
      },
      {
        title: "Dispute-to-Case Documentation",
        text: "Move from credit report review to dispute tracking, evidence organization, response history, and case-file preparation for attorney review when legal escalation may be appropriate.",
      },
      {
        title: "Case Files",
        text: "Organize dispute records, supporting documentation, account details, correspondence, timelines, and case information in one centralized system.",
      },
      {
        title: "Team & Work Queue",
        text: "Assign clients, manage staff workflows, track tasks, and keep your operation organized as your business grows.",
      },
    ];

  const plans = [
    {
      name: "Starter",
      price: "$99",
      note: "per month after your 7-day free trial",
      items: [
        "Client management",
        "AI-assisted credit report workflow",
        "Dispute letter management",
        "Documents & tasks",
        "Dashboard & activity tracking",
      ],
    },
    {
      name: "Professional",
      price: "$199",
      note: "per month after your 7-day free trial",
      items: [
        "Everything in Starter",
        "Case Files & dispute-to-case documentation",
        "Response & evidence organization",
        "Advanced analytics",
        "Employee management",
        "Expanded workflow tools",
      ],
    },
    {
      name: "White Label",
      price: "$399",
      note: "per month after your 7-day free trial",
      items: [
        "Everything in Professional",
        "White-label branding",
        "Custom business identity",
        "Advanced team access",
        "Premium platform controls",
        "Client-facing branded experience",
      ],
    },
  ];

  const sectionStyle = {
    maxWidth: "1180px",
    margin: "0 auto",
    padding: "84px 24px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #050505 0%, #0b0b0b 42%, #111111 100%)",
        color: "#ffffff",
        fontFamily:
          'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(5,5,5,0.94)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(212,175,55,0.18)",
        }}
      >
        <div
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "20px",
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              textDecoration: "none",
              color: "#fff",
            }}
          >
            <img
              src={logo}
              alt="Integrity Suite CRM"
              style={{
                width: "48px",
                height: "48px",
                objectFit: "contain",
              }}
            />
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 900,
                  letterSpacing: "-0.02em",
                }}
              >
                Integrity Suite CRM
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#d4af37",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                }}
              >
                Credit Repair Management Platform
              </div>
            </div>
          </Link>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <a
              href="#features"
              style={{
                color: "#d7d7d7",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              Features
            </a>

            <a
              href="#pricing"
              style={{
                color: "#d7d7d7",
                textDecoration: "none",
                fontWeight: 700,
              }}
            >
              Pricing
            </a>

            <Link
              to="/login"
              style={{
                padding: "10px 16px",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "10px",
                color: "#fff",
                textDecoration: "none",
                fontWeight: 800,
              }}
            >
              Sign In
            </Link>

            <Link
              to="/register"
              style={{
                padding: "11px 18px",
                borderRadius: "10px",
                color: "#050505",
                background: "#d4af37",
                textDecoration: "none",
                fontWeight: 900,
              }}
            >
              Start 7-Day Free Trial
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section
          style={{
            ...sectionStyle,
            paddingTop: "110px",
            paddingBottom: "110px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "56px",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-block",
                padding: "8px 12px",
                borderRadius: "999px",
                border: "1px solid rgba(212,175,55,0.35)",
                color: "#d4af37",
                fontSize: "13px",
                fontWeight: 800,
                marginBottom: "20px",
              }}
            >
              Built for credit repair operations
            </div>

            <h1
              style={{
                fontSize: "clamp(42px, 6vw, 76px)",
                lineHeight: 0.98,
                margin: "0 0 24px",
                letterSpacing: "-0.05em",
                fontWeight: 950,
              }}
            >
              Run Your Credit Repair Business Smarter.
            </h1>

            <p
              style={{
                fontSize: "20px",
                lineHeight: 1.7,
                color: "#c7c7c7",
                maxWidth: "720px",
                marginBottom: "30px",
              }}
            >
              Integrity Suite CRM brings client management, credit report
              analysis, dispute workflows, letter generation, case tracking,
              team tools, and AI-assisted strategy into one secure platform.
            </p>

            <div
              style={{
                display: "flex",
                gap: "14px",
                flexWrap: "wrap",
                marginBottom: "28px",
              }}
            >
              <Link
                to="/register"
                style={{
                  padding: "15px 22px",
                  borderRadius: "12px",
                  background: "#d4af37",
                  color: "#050505",
                  textDecoration: "none",
                  fontWeight: 900,
                  fontSize: "16px",
                }}
              >
                Start 7-Day Free Trial
              </Link>

              <Link
                to="/login"
                style={{
                  padding: "15px 22px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  color: "#fff",
                  textDecoration: "none",
                  fontWeight: 900,
                  fontSize: "16px",
                }}
              >
                Existing Customer Sign In
              </Link>
            </div>

            <p
              style={{
                margin: 0,
                color: "#8f8f8f",
                fontSize: "14px",
                lineHeight: 1.7,
              }}
            >
              Software platform for business workflow management. Integrity
              Suite CRM does not guarantee deletions, score increases, or
              specific credit outcomes.
            </p>
          </div>

          <div
            style={{
              background:
                "linear-gradient(145deg, rgba(212,175,55,0.12), rgba(255,255,255,0.03))",
              border: "1px solid rgba(212,175,55,0.22)",
              borderRadius: "28px",
              padding: "34px",
              boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "16px",
              }}
            >
              {[
                ["AI Analysis", "Identify review opportunities faster"],
                ["Letters", "Manage dispute rounds and follow-ups"],
                ["Clients", "Keep every case organized"],
                ["Workflow", "Track tasks, disputes, and activity"],
              ].map(([title, text]) => (
                <div
                  key={title}
                  style={{
                    padding: "22px",
                    background: "rgba(0,0,0,0.36)",
                    borderRadius: "18px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <div
                    style={{
                      color: "#d4af37",
                      fontWeight: 900,
                      fontSize: "16px",
                      marginBottom: "8px",
                    }}
                  >
                    {title}
                  </div>
                  <div
                    style={{
                      color: "#bdbdbd",
                      lineHeight: 1.55,
                      fontSize: "14px",
                    }}
                  >
                    {text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Demo */}
        <section
          id="demo"
          style={{
            background: "#080808",
            color: "#fff",
            borderTop: "1px solid rgba(212,175,55,0.16)",
            borderBottom: "1px solid rgba(212,175,55,0.16)",
          }}
        >
          <div
            style={{
              ...sectionStyle,
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#d4af37",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontSize: "13px",
                marginBottom: "12px",
              }}
            >
              Product Demo
            </div>

            <h2
              style={{
                fontSize: "clamp(34px, 5vw, 54px)",
                margin: "0 0 16px",
                letterSpacing: "-0.04em",
              }}
            >
              See Integrity Suite CRM in Action
            </h2>

            <p
              style={{
                color: "#bdbdbd",
                fontSize: "18px",
                lineHeight: 1.7,
                maxWidth: "760px",
                margin: "0 auto 36px",
              }}
            >
              From Credit Report to Case File — see how Integrity Suite CRM
              brings analysis, dispute strategy, letter generation, workflow
              tracking, and case documentation together in one platform.
            </p>

            <div
              style={{
                maxWidth: "1100px",
                margin: "0 auto",
                padding: "10px",
                borderRadius: "22px",
                background:
                  "linear-gradient(145deg, rgba(212,175,55,0.22), rgba(255,255,255,0.04))",
                border: "1px solid rgba(212,175,55,0.35)",
                boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/5KjLIrgbiX0"
                title="Integrity Suite CRM Official Demo | From Credit Report to Case File"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  display: "block",
                  width: "100%",
                  aspectRatio: "16 / 9",
                  border: "0",
                  borderRadius: "14px",
                  background: "#000",
                }}
              />
            </div>

            <div
              style={{
                marginTop: "32px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Link
                to="/register"
                style={{
                  padding: "15px 26px",
                  borderRadius: "12px",
                  background: "#d4af37",
                  color: "#050505",
                  textDecoration: "none",
                  fontWeight: 900,
                  fontSize: "16px",
                }}
              >
                Start 7-Day Free Trial
              </Link>
            </div>
          </div>
        </section>

        <section
          id="features"
          style={{
            background: "#f7f7f7",
            color: "#111",
          }}
        >
          <div style={sectionStyle}>
            <div
              style={{
                maxWidth: "760px",
                marginBottom: "44px",
              }}
            >
              <div
                style={{
                  color: "#9a7a12",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "13px",
                  marginBottom: "10px",
                }}
              >
                Platform Features
              </div>

              <h2
                style={{
                  fontSize: "clamp(34px, 5vw, 52px)",
                  margin: "0 0 16px",
                  letterSpacing: "-0.04em",
                }}
              >
                One system for the work that keeps your business moving.
              </h2>

              <p
                style={{
                  color: "#555",
                  fontSize: "18px",
                  lineHeight: 1.7,
                }}
              >
                Replace scattered spreadsheets, files, and disconnected tools
                with a centralized workflow built around credit repair
                operations.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "20px",
              }}
            >
              {features.map((feature) => (
                <div
                  key={feature.title}
                  style={{
                    padding: "28px",
                    borderRadius: "18px",
                    background: "#fff",
                    border: "1px solid #e5e5e5",
                    boxShadow: "0 10px 28px rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "12px",
                      background: "#111",
                      color: "#d4af37",
                      fontWeight: 950,
                      marginBottom: "18px",
                    }}
                  >
                    ✓
                  </div>

                  <h3
                    style={{
                      margin: "0 0 10px",
                      fontSize: "20px",
                    }}
                  >
                    {feature.title}
                  </h3>

                  <p
                    style={{
                      margin: 0,
                      color: "#606060",
                      lineHeight: 1.7,
                    }}
                  >
                    {feature.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

                <section>
                  <div style={sectionStyle}>
                    <div
                      style={{
                        textAlign: "center",
                        maxWidth: "860px",
                        margin: "0 auto 50px",
                      }}
                    >
                      <div
                        style={{
                          color: "#d4af37",
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "0.12em",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                       >
                         How It Works
                       </div>

                       <h2
                         style={{
                           fontSize: "clamp(34px, 5vw, 52px)",
                           margin: "0 0 16px",
                           letterSpacing: "-0.04em",
                         }}
                       >
                         From credit report to documented case workflow.
                       </h2>

                       <p
                         style={{
                           margin: "0 auto",
                           maxWidth: "760px",
                           color: "#a9a9a9",
                           fontSize: "17px",
                           lineHeight: 1.8,
                         }}
                       >
                         Integrity Suite helps credit repair professionals manage the
                         entire dispute lifecycle — from credit analysis and dispute
                         strategy through response tracking, potential compliance
                         issues, and organized case documentation.
                       </p>
                     </div>

                     <div
                       style={{
                         display: "grid",
                         gridTemplateColumns:
                           "repeat(auto-fit, minmax(230px, 1fr))",
                         gap: "20px",
                       }}
                     >
                       {[
                         [
                           "01",
                           "Add Your Client",
                           "Create the client profile and organize case information in one workspace.",
                         ],
                         [
                           "02",
                           "Analyze Credit Data",
                           "Upload and review credit report information with AI-assisted account analysis.",
                         ],
                         [
                           "03",
                           "Build the Dispute Strategy",
                           "Review account issues, dispute candidates, recommended actions, and supporting information.",
                         ],
                         [
                           "04",
                           "Send & Track Disputes",
                           "Generate dispute letters, manage dispute rounds, and track important follow-up dates.",
                         ],
                         [
                           "05",
                           "Document Responses",
                           "Record bureau and furnisher responses, account changes, correspondence, and supporting evidence.",
                         ],
                         [
                           "06",
                           "Flag Potential Compliance Issues",
                           "Document potential credit reporting compliance concerns that may require additional review.",
                         ],
                         [
                           "07",
                           "Build the Case File",
                           "Organize disputes, responses, timelines, documents, and supporting records for attorney review when escalation may be appropriate.",
                         ],
                       ].map(([number, title, text]) => (
                         <div
                           key={number}
                           style={{
                             padding: "28px",
                             borderRadius: "18px",
                             border: "1px solid rgba(255,255,255,0.1)",
                             background: "rgba(255,255,255,0.03)",
                          }}
                        >
                          <div
                            style={{
                            color: "#d4af37",
                            fontSize: "14px",
                            fontWeight: 900,
                            marginBottom: "12px",
                          }}
                         >
                          {number}
                        </div>

                        <h3
                          style={{
                            fontSize: "21px",
                            margin: "0 0 10px",
                          }}
                        >
                          {title}
                        </h3>

                        <p
                          style={{
                            margin: 0,
                            color: "#a9a9a9",
                            lineHeight: 1.7,
                          }}
                        >
                          {text}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div
                    style={{
                      marginTop: "30px",
                      padding: "20px 22px",
                      borderRadius: "16px",
                      border: "1px solid rgba(212,175,55,0.3)",
                      background: "rgba(212,175,55,0.06)",
                      color: "#c8c8c8",
                      fontSize: "14px",
                      lineHeight: 1.7,
                      textAlign: "center",
                    }}
                  >
                    Integrity Suite provides workflow, documentation, and case
                    organization tools. It does not provide legal advice, legal
                    representation, or determine that a legal violation has
                    occurred.
                  </div>
                </div>
              </section>

        <section
          id="pricing"
          style={{
            background: "#f7f7f7",
            color: "#111",
          }}
        >
          <div style={sectionStyle}>
            <div
              style={{
                textAlign: "center",
                maxWidth: "760px",
                margin: "0 auto 46px",
              }}
            >
              <div
                style={{
                  color: "#9a7a12",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontSize: "13px",
                  marginBottom: "10px",
                }}
              >
                Pricing
              </div>

              <h2
                style={{
                  fontSize: "clamp(34px, 5vw, 52px)",
                  margin: "0 0 14px",
                  letterSpacing: "-0.04em",
                }}
              >
                Choose the plan that fits your operation.
              </h2>
              <p>New companies get 7 days free on their selected plan. A payment method is required. Monthly billing begins after the trial unless you cancel in Settings &gt; Billing before it ends.</p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "22px",
              }}
            >
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  style={{
                    padding: "30px",
                    borderRadius: "20px",
                    background: "#fff",
                    border:
                      plan.name === "Professional"
                        ? "2px solid #d4af37"
                        : "1px solid #dedede",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "24px",
                      margin: "0 0 16px",
                    }}
                  >
                    {plan.name}
                  </h3>

                  <div
                    style={{
                      fontSize:
                        plan.price === "Coming Soon" ? "24px" : "44px",
                      fontWeight: 950,
                      marginBottom: "4px",
                    }}
                  >
                    {plan.price}
                  </div>

                  {plan.note && (
                    <div
                      style={{
                        color: "#777",
                        marginBottom: "22px",
                      }}
                    >
                      {plan.note}
                    </div>
                  )}

                  <div
                    style={{
                      marginTop: "22px",
                      display: "grid",
                      gap: "12px",
                    }}
                  >
                    {plan.items.map((item) => (
                      <div
                        key={item}
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "flex-start",
                          color: "#444",
                        }}
                      >
                        <strong
                          style={{
                            color: "#9a7a12",
                          }}
                        >
                          ✓
                        </strong>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/register"
                    style={{
                      display: "block",
                      marginTop: "28px",
                      textAlign: "center",
                      padding: "13px 16px",
                      borderRadius: "10px",
                      textDecoration: "none",
                      fontWeight: 900,
                      background:
                        plan.name === "Professional"
                          ? "#d4af37"
                          : "#111",
                      color:
                        plan.name === "Professional"
                          ? "#050505"
                          : "#fff",
                    }}
                  >
                    Get Started
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section>
          <div
            style={{
              ...sectionStyle,
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: "clamp(34px, 5vw, 54px)",
                margin: "0 auto 18px",
                maxWidth: "800px",
                letterSpacing: "-0.04em",
              }}
            >
              Build a more organized credit repair operation.
            </h2>

            <p
              style={{
                maxWidth: "720px",
                margin: "0 auto 28px",
                color: "#aaa",
                fontSize: "18px",
                lineHeight: 1.7,
              }}
            >
              Bring your clients, credit-report review, disputes, letters,
              documents, tasks, and team workflow together in Integrity Suite
              CRM.
            </p>

            <Link
              to="/register"
              style={{
                display: "inline-block",
                padding: "15px 24px",
                borderRadius: "12px",
                background: "#d4af37",
                color: "#050505",
                textDecoration: "none",
                fontWeight: 950,
              }}
            >
              Start 7-Day Free Trial
            </Link>
          </div>
        </section>
      </main>

      <footer
        style={{
          borderTop: "1px solid rgba(255,255,255,0.08)",
          padding: "34px 24px",
          color: "#8d8d8d",
        }}
      >
        <div
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            display: "flex",
            gap: "20px",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            © {new Date().getFullYear()} Integrity Suite CRM. All rights
            reserved.
          </div>

          <div
            style={{
              display: "flex",
              gap: "18px",
              flexWrap: "wrap",
            }}
          >
          <Link
            to="/contact"
            style={{ color: "#b8b8b8", textDecoration: "none" }}
          >
            Contact
          </Link>

          <Link
            to="/privacy"
            style={{ color: "#b8b8b8", textDecoration: "none" }}
          >
            Privacy
          </Link>

          <Link
            to="/terms"
            style={{ color: "#b8b8b8", textDecoration: "none" }}
          >
            Terms
          </Link>

         <Link
           to="/refunds"
           style={{ color: "#b8b8b8", textDecoration: "none" }}
         >
           Refunds & Cancellation
</Link>

<Link
  to="/login"
  style={{ color: "#b8b8b8", textDecoration: "none" }}
>
  Sign In
</Link>

<Link
  to="/register"
  style={{ color: "#b8b8b8", textDecoration: "none" }}
>
  Create Account
</Link> 
          </div>
        </div>

        <div
          style={{
            maxWidth: "1180px",
            margin: "24px auto 0",
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 24px",
            fontSize: "14px",
            overflowWrap: "anywhere",
          }}
        >
          <a href="mailto:support@integritysuitecrm.com" style={{ color: "#b8b8b8" }}>
            support@integritysuitecrm.com
          </a>
          <a href="tel:+19096195191" style={{ color: "#b8b8b8" }}>
            (909) 619-5191
          </a>
          <a href="https://www.integritysuitecrm.com" style={{ color: "#b8b8b8" }}>
            www.integritysuitecrm.com
          </a>
        </div>
      </footer>
    </div>
  );
}
